import {useCallback, useEffect, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';

import {useApiRead} from './useApiRead';

const PER_PAGE = 20;
const PAGE = 1;

export type FilterObject = Partial<Record<string, string | number>>;

type Filters<T extends FilterObject> = T;

export interface PaginationResponse<T, Count, FilterMeta = unknown> {
  data: T[];
  // @TODO move this data into a separate api call. It should be separated to avoid mixing pagination logic with domain
  // specific logic.
  totalCountPerStatus?: Count;
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  /** Optional endpoint-specific metadata (e.g. filter options). Typed per call site via `FilterMeta`. */
  filterData?: FilterMeta;
}
interface PaginationArgs<T extends FilterObject, FilterMeta = unknown> {
  entity: string;
  url: string;
  filterParams?: Filters<T>;
  perPage?: number;
  initialPage?: number;
  reverseOrder?: boolean;
  enabled?: boolean;
  /** When set, replaces default body camelCase with a domain-specific mapper (e.g. nested `talent_pool`). */
  mapResponse?: (raw: unknown) => PaginationResponse<unknown, Record<string, string | number>, FilterMeta>;
}

export const useQueryPagination = <
  Entity extends {resourceId: string},
  Count extends Record<string, string | number>,
  Filters extends FilterObject = object,
  FilterMeta = unknown
>({
    url,
    entity,
    filterParams,
    perPage = PER_PAGE,
    initialPage = PAGE,
    reverseOrder,
    enabled = true,
    mapResponse,
  }: PaginationArgs<Filters, FilterMeta>) => {
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState<Entity[]>([]);
  const [queryParams, setQueryParams] = useState<string>('');
  const client = useQueryClient();

  useEffect(() => {
    // initial values on load
    setPage(initialPage);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPage(1);
    setLoadingInitialData(true);
    setPaginatedData([]);
  }, [filterParams]);

  useEffect(() => {
    let params = '';
    if (filterParams) {
      Object.keys(filterParams).forEach((key) => {
        const value = filterParams[key];
        if (value === undefined || value === null) {
          return;
        }
        params = `&${key}=${encodeURIComponent(String(value))}${params}`;
      });
    }

    setQueryParams(params);
  }, [filterParams]);

  const {
    data: response,
    isLoading,
    error,
    isPending,
    isFetching,
    isFetched,
  } = useApiRead<PaginationResponse<Entity, Count, FilterMeta>>({
    path: `${url}?page=${page}&per_page=${perPage}${queryParams}`,
    camelCaseOverride: mapResponse
      ? (raw) => mapResponse(raw) as PaginationResponse<Entity, Count, FilterMeta>
      : undefined,
    options: {
      queryKey: [entity, page, queryParams],
      enabled,
    },
  });

  const hasNext = !!response?.pagination?.nextPage;

  const hasPrevious = !!response?.pagination?.previousPage;

  const getNext = useCallback(() => {
    setReloading(true);
    if (response?.pagination?.nextPage) {
      setPage(response.pagination.nextPage);
    }
  }, [response?.pagination]);

  const getPrevious = useCallback(() => {
    if (response?.pagination?.previousPage) {
      setPage(response.pagination.previousPage);
    }
  }, [response?.pagination]);

  const mergePaginatedDataFromResponse = useCallback(
    (data: Entity[]) => {
      const setId = (map: Map<string, Entity>, entry: Entity) => {
        const id = entry.resourceId?.trim();
        if (id) {
          map.set(id, entry);
        }
      };

      setPaginatedData((prev) => {
        if (!data.length) {
          if (page === 1) {
            return [];
          }
          return prev;
        }

        // Page 1 must always replace local data. This avoids stale/incorrect datasets
        // when query params change and the new page-1 response shares IDs with old data.
        if (page === 1) {
          const firstPageMap = new Map<string, Entity>();
          data.forEach((entry) => setId(firstPageMap, entry));
          return [...firstPageMap.values()];
        }

        const dataMap = new Map<string, Entity>();
        // Filters out potential duplicates while preserving configured ordering.
        if (reverseOrder) {
          prev.forEach((entry) => setId(dataMap, entry));
          data.forEach((entry) => setId(dataMap, entry));
        } else {
          data.forEach((entry) => setId(dataMap, entry));
          prev.forEach((entry) => setId(dataMap, entry));
        }
        return [...dataMap.values()];
      });
      setReloading(false);
    },
    [reverseOrder, page]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (Array.isArray(response?.data)) {
      mergePaginatedDataFromResponse(response.data);
    }
  }, [response?.data, mergePaginatedDataFromResponse, isLoading]);

  const refreshData = useCallback(() => {
    setPaginatedData([]);
    setPage(initialPage);
    // resets cache to initial state for all queries beginning with the `entity`.
    client.resetQueries({queryKey: [entity]});
  }, [initialPage, client, entity]);

  useEffect(() => {
    if (!isPending && loadingInitialData) {
      setLoadingInitialData(false);
    }
  }, [isPending, loadingInitialData]);

  return {
    data: paginatedData,
    pagination: response?.pagination,
    totalCountPerStatus: response?.totalCountPerStatus,
    filterData: response?.filterData,
    isLoading: isLoading || reloading,
    loadingInitialData,
    error,
    getNext,
    getPrevious,
    hasNext,
    hasPrevious,
    isFetching,
    isFetched,
    isPending,
    refreshData,
  };
};
